import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  capture,
  getStoredConsent,
  grantConsent,
  revokeConsent,
} from "@/lib/analytics";
import { EVENTS, deviceType } from "@/lib/analyticsEvents";

export default function CookieConsent() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const shownAt = useRef(null);
  const panel = useRef(null);

  useEffect(() => {
    // Show the banner only when no consent decision has been made yet.
    if (getStoredConsent() === null) {
      setVisible(true);
      shownAt.current = Date.now();
      // Defer the slide-in by a frame so the transition plays.
      requestAnimationFrame(() => setMounted(true));
    }
  }, []);

  // Le document se termine au-dessus de la bannière tant qu'elle est affichée.
  // Sans cette réserve, sur un téléphone, elle recouvrait la première réponse
  // du formulaire d'enregistrement : le doigt atterrissait sur la bannière, et
  // en bas de page aucun défilement ne pouvait dégager ce qui passait dessous.
  // La hauteur est mesurée, jamais devinée : elle dépend de la largeur et de la
  // langue, et un observateur la suit si le texte se replie autrement.
  useEffect(() => {
    const el = panel.current;
    if (!visible || !el) return;
    const apply = () =>
      document.documentElement.style.setProperty(
        "--consent-space",
        `${Math.ceil(el.getBoundingClientRect().height)}px`
      );
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--consent-space");
    };
  }, [visible]);

  const close = () => {
    setMounted(false);
    setTimeout(() => setVisible(false), 300);
  };

  const accept = () => {
    // L'ordre est ce qui rend l'événement possible : grantConsent() réactive
    // la capture, sans quoi celui-ci partirait dans le vide.
    grantConsent();
    capture(EVENTS.CONSENT_GRANTED, {
      device_type: deviceType(),
      decision_ms: shownAt.current ? Date.now() - shownAt.current : undefined,
      landing_path: window.location.pathname,
    });
    close();
  };

  // Refuser et fermer mènent au même endroit, et aucun des deux n'émet quoi que
  // ce soit : la capture est désactivée à cet instant, et la rallumer pour
  // enregistrer un refus contredirait le refus lui-même.
  const decline = () => {
    revokeConsent();
    close();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-label={t("cookie.aria")}
      ref={panel}
      // La marge transparente autour du panneau laisse passer le doigt : sans
      // `pointer-events-none`, elle interceptait les taps sur le contenu qu'on
      // voyait pourtant à travers elle.
      className={`fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6 pointer-events-none transition-all duration-300 ease-out ${
        mounted ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
      }`}
    >
      <div className="max-w-4xl mx-auto relative pointer-events-auto bg-surface/95 backdrop-blur-xl border border-gold/30 shadow-2xl shadow-shade/60">
        <div className="absolute top-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-5 p-4 md:p-6">
          <div className="flex items-start gap-3 md:gap-4 flex-1">
            <Cookie size={22} className="text-gold-ink shrink-0 mt-0.5" />
            <p className="text-sm text-ink-muted leading-relaxed">
              {/* Deux longueurs pour un même fond. Sur un téléphone, le texte
                  long portait la bannière à 298 px sur 809, soit 37 % de
                  l'écran, juste là où s'affiche le résultat d'une consultation
                  et le dossier qu'on envoie à un marchand. La version courte
                  nomme toujours ce qui est mesuré, par qui, où, et qu'aucune
                  mesure ne part sans accord ; le détail est à un lien. */}
              <span className="sm:hidden">{t("cookie.textShort")}</span>
              <span className="hidden sm:inline">
                {t("cookie.textPre")}
                <span className="text-ink-body">{t("cookie.textStrong")}</span>
                {t("cookie.textPost")}
              </span>{" "}
              <Link
                to="/politique-cookies"
                data-testid="cookie-consent-link"
                className="text-gold-ink underline underline-offset-2 hover:text-gold-ink transition-colors"
              >
                {t("cookie.learnMore")}
              </Link>
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 md:pl-4">
            <button
              type="button"
              data-testid="cookie-consent-accept"
              onClick={accept}
              className="inline-flex items-center justify-center gap-2 bg-gold-strong text-on-gold px-6 py-2.5 text-xs tracking-[0.18em] uppercase font-semibold hover:bg-gold-muted transition-colors duration-300"
            >
              {t("cookie.accept")}
            </button>
            <button
              type="button"
              data-testid="cookie-consent-decline"
              onClick={decline}
              className="inline-flex items-center justify-center gap-2 border border-ink/15 text-ink-muted px-6 py-2.5 text-xs tracking-[0.18em] uppercase hover:text-ink hover:border-ink/30 transition-colors duration-300"
            >
              {t("cookie.decline")}
            </button>
            <button
              type="button"
              data-testid="cookie-consent-close"
              onClick={decline}
              aria-label={t("cookie.close")}
              className="text-ink-subtle hover:text-gold-ink transition-colors p-1.5"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
